
  import { defineComponent, onUpdated, PropType, ref, toRefs, watch } from 'vue';
  import {
    BaseOverlay,
    TheMasonry,
    BaseIcon,
    GetEntityByIdDocument,
    BaseButton,
    useBoxVisiter,
    useMediaModal,
  } from 'coghent-vue-3-component-library';
  import { useQuery, useMutation } from '@vue/apollo-composable';
  import { Relation, Entity } from 'coghent-vue-3-component-library/lib/queries';
  import { iiiF, apolloClient } from '@/main';
  import Spinner from './Spinner.vue';
  import { useI18n } from 'vue-i18n';
  import { IIIFImageUrlHelper } from '@/services/Fabric/helper.fabric';
  import { getFileNameByMimeType } from 'coghent-vue-3-component-library';

  export type OverlayState = 'show' | 'hide' | 'loading';

  export type BasketOverlayType = {
    state: OverlayState;
    overlayItems: Relation[];
  };

  const BasketOverlayState = ref<BasketOverlayType>({
    state: 'hide',
    overlayItems: [],
  });

  export const useBasketOverlay = () => {
    const updateBasketOverlayState = (BasketOverlayInput: OverlayState) => {
      BasketOverlayState.value.state = BasketOverlayInput;
    };

    const addBasketOverlayItems = (basketOverlayItems: Relation[]) => {
      BasketOverlayState.value.overlayItems.push(...basketOverlayItems);
    };

    const updateBasketOverlayItems = (basketOverlayItems: Relation[]) => {
      BasketOverlayState.value.overlayItems = basketOverlayItems;
    };

    const closeBasketOverlay = () => {
      updateBasketOverlayState('hide');
    };

    const openBasketOverlay = () => {
      updateBasketOverlayState('show');
    };

    return {
      closeBasketOverlay,
      openBasketOverlay,
      addBasketOverlayItems,
      updateBasketOverlayItems,
      BasketOverlayState,
    };
  };

  export default defineComponent({
    name: 'BasketOverlay',
    components: {
      BaseOverlay,
      TheMasonry,
      BaseIcon,
      Spinner,
      BaseButton,
    },
    props: {
      boxVisitorCode: {
        type: String,
        required: true,
      },
    },
    setup(props) {
      const {
        closeBasketOverlay,
        openBasketOverlay,
        BasketOverlayState,
        updateBasketOverlayItems,
      } = useBasketOverlay();
      const { generateUrl, noImageUrl } = iiiF;
      const basketEntities = ref<Entity[]>([]);
      const { t } = useI18n();
      const { openMediaModal, setMediaModalFile, setMediaModalImageUrl } =
        useMediaModal();
      const masonry = ref<any>(null);

      const { loading: loadingEntity, fetchMore: fetchMoreEntities } = useQuery(
        GetEntityByIdDocument,
        { id: '' },
      );

      const getEntitiesForRelations = (entitiesToAdd: any[] = []) => {
        const tempEntityArray: any[] = [];
        let basketEntityIds: string[] = [];
        if (entitiesToAdd.length) {
          basketEntityIds = entitiesToAdd.map((item: Relation) =>
            item.key.replace('entities/', ''),
          );
        }
        basketEntityIds.forEach((basketEntityId: string) => {
          fetchMoreEntities({
            variables: { id: basketEntityId },
            updateQuery: (previousData, { fetchMoreResult }) => {
              if (fetchMoreResult.Entity) {
                tempEntityArray.push(fetchMoreResult.Entity);
                if (entitiesToAdd.length == tempEntityArray.length) {
                  basketEntities.value.push(...tempEntityArray);
                }
              }
            },
          });
        });
      };

      watch(
        () => BasketOverlayState?.value?.overlayItems?.length,
        () => {
          if (
            BasketOverlayState.value.overlayItems &&
            BasketOverlayState.value.overlayItems.length
          ) {
            if (!basketEntities.value.length) {
              getEntitiesForRelations(BasketOverlayState.value.overlayItems);
            } else {
              const newEntities = BasketOverlayState.value.overlayItems.filter(
                (item: any) =>
                  !basketEntities.value.find(
                    (basketItem: any) =>
                      basketItem.id == item.key.replace('entities/', ''),
                  ),
              );
              if (newEntities.length) {
                getEntitiesForRelations(newEntities);
              }
            }
          }
        },
        { immediate: true },
      );

      const removeFromBasket = (entityId: string) => {
        const newBasket = basketEntities.value.filter(
          (basketEntity: Entity) => basketEntity.id != entityId,
        );
        basketEntities.value = newBasket;
        const { deleteBasketItemFromBoxVisiter } = useBoxVisiter(apolloClient);
        deleteBasketItemFromBoxVisiter(props.boxVisitorCode, entityId).then(
          (result: Relation[]) => {
            updateBasketOverlayItems(result);
          },
        );
      };

      const openMediaOverlay = (entity: any) => {
        setMediaModalFile(entity.mediafiles[0]);
        setMediaModalImageUrl(
          IIIFImageUrlHelper(getFileNameByMimeType(entity.mediafiles[0])),
        );
        openMediaModal();
      };

      return {
        closeBasketOverlay,
        openBasketOverlay,
        BasketOverlayState,
        generateUrl,
        noImageUrl,
        basketEntities,
        loadingEntity,
        openMediaOverlay,
        removeFromBasket,
        masonry,
        t,
      };
    },
  });
